import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Phone, ChevronDown } from "lucide-react";

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-200 shadow-sm" : "bg-transparent border-transparent"}`}
    >
      <div className="container mx-auto flex h-20 items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Link to="/">
            <img
              src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
              alt="Clean Marketing Logo"
              fetchPriority="high"
              loading="eager"
              width="200"
              height="48"
              className="h-10 md:h-12 w-auto"
            />
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className="hidden lg:flex gap-6 xl:gap-8 text-sm font-medium text-secondary">
          <Link to="/#stats" className="hover:text-[#e04825] transition-colors">
            Results
          </Link>
          <Link
            to="/#features"
            className="hover:text-[#e04825] transition-colors"
          >
            Features
          </Link>
          <Link
            to="/#coverage"
            className="hover:text-[#e04825] transition-colors"
          >
            Where We Cover
          </Link>
          <Link
            to="/#testimonials"
            className="hover:text-[#e04825] transition-colors"
          >
            Testimonials
          </Link>
          <Link to="/#faq" className="hover:text-[#e04825] transition-colors">
            FAQ
          </Link>

          <Link
            to="/#pricing"
            className="hover:text-[#e04825] transition-colors"
          >
            Pricing
          </Link>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 sm:gap-4">
          <a
            href="tel:01225234456"
            className="hidden md:flex text-sm font-bold text-secondary hover:text-[#e04825] transition-colors items-center gap-1.5 mr-2"
          >
            <Phone className="h-4 w-4" />
            01225 234456
          </a>
          <Button
            variant="ghost"
            size="icon"
            asChild
            className="md:hidden text-secondary bg-slate-100 hover:bg-[#e04825] hover:text-white transition-colors rounded-xl h-10 w-10"
          >
            <a href="tel:01225234456">
              <Phone className="h-5 w-5" />
              <span className="sr-only">Call 01225 234456</span>
            </a>
          </Button>

          {/* Desktop Only Links */}
          <div className="hidden lg:flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-secondary hover:text-[#e04825] transition-colors focus:outline-none">
                Login <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-40 bg-white border-slate-200 shadow-md rounded-xl"
              >
                <DropdownMenuItem
                  asChild
                  className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 text-secondary hover:text-[#e04825] focus:text-[#e04825] font-medium py-2"
                >
                  <a href="https://app.cleanmarketing.co.uk/">Dashboard</a>
                </DropdownMenuItem>
                <DropdownMenuItem
                  asChild
                  className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 text-secondary hover:text-[#e04825] focus:text-[#e04825] font-medium py-2"
                >
                  <a href="https://client.cleanmarketing.co.uk/">App</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/signup"
              className="text-sm font-medium text-secondary hover:text-[#e04825] transition-colors"
            >
              Sign Up
            </Link>
          </div>

          {/* Book Demo - Hidden on mobile, visible on tablet & desktop */}
          <Button
            className="hidden sm:flex bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors"
            asChild
          >
            <Link to="/book-demo">Book a Demo</Link>
          </Button>

          {/* Mobile/Tablet Menu */}
          <div className="flex lg:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  size="icon"
                  className="bg-primary text-white hover:bg-[#e04825] transition-colors rounded-xl h-10 w-10 shadow-sm"
                >
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[300px] bg-white border-l border-slate-200"
              >
                <div className="flex flex-col gap-6 mt-12">
                  <Link
                    to="/#stats"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Results
                  </Link>
                  <Link
                    to="/#features"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Features
                  </Link>
                  <Link
                    to="/#coverage"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Where We Cover
                  </Link>
                  <Link
                    to="/#testimonials"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Testimonials
                  </Link>
                  <Link
                    to="/#faq"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    FAQ
                  </Link>

                  <Link
                    to="/#pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Pricing
                  </Link>
                  <div className="h-px bg-slate-100 my-2"></div>
                  <a
                    href="https://app.cleanmarketing.co.uk/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Login to Dashboard
                  </a>
                  <a
                    href="https://client.cleanmarketing.co.uk/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-secondary hover:text-[#e04825] transition-colors"
                  >
                    Login to App
                  </a>
                  <Link
                    to="/signup"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg font-semibold text-primary hover:text-[#e04825] transition-colors"
                  >
                    Sign Up
                  </Link>
                  <Button
                    className="w-full mt-4 bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors sm:hidden"
                    asChild
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Link to="/book-demo">Book a Demo</Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
